import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useLanguage } from '../../Shared/cache/context/LanguageProvider';
import { EN } from '../../Shared/constants/constant';
import { setSessionData } from '../../Shared/helper/storage';
import { getLangData, getProductConfigData } from '../api';
import { CONFIG_DATA_TEXT, CONFIG_KEY, LANG_DATA_TEXT } from '../GlobalConstant';
import { getProductCode, getSessionData } from '../utills';

export const TransAndConfigContext = createContext();

export const ConfigAndTranslationProvider = ({ children }) => {

  const [searchParams] = useSearchParams();
  const [configData, setConfigData] = useState(() => {
    return getSessionData(CONFIG_DATA_TEXT)
  });
  const [langData, setLangData] = useState(() => {
    return getSessionData(LANG_DATA_TEXT)
  });
  const [pendingRequests, setPendingRequests] = useState(1);


  const navigate = useNavigate();
  const location = useLocation();
  const { selectedLanguage } = useLanguage();

  const productCodes = useMemo(() => getProductCode(location?.pathname, searchParams), [location?.pathname, searchParams]);
  const productCode = productCodes[0];
  const productCodesKey = productCodes.join(",");

  const startLoading = () =>
    setPendingRequests(count => count + 1);

  const stopLoading = () =>
    setPendingRequests(count => Math.max(0, count - 1));

  const fetchConfigData = useCallback(async (productCodeValue, isOptional = false) => {
    if (!(productCode || productCodeValue))
      return
    startLoading()
    try {
      const configRes = await getProductConfigData(productCodeValue ?? productCode);
      setConfigData(prev => {
        const sessionData = {
          ...prev,
          [productCodeValue ?? productCode]: configRes,
        };
        setSessionData(CONFIG_DATA_TEXT, sessionData)
        return sessionData;
      })
      stopLoading()
    } catch (err) {
      if (isOptional) {
        setConfigData(prev => ({ ...prev, [productCodeValue]: {} }));
        stopLoading();
        return;
      }
      navigate('/');
    }
  }, [productCode, navigate]);

  const fetchLangData = useCallback(async (lang = EN) => {
    startLoading()
    try {
      const headers = {
        "Accept-language": lang
      }
      const langRes = await getLangData(headers);
      setLangData(prev => {
        const updated = { ...prev, [lang]: langRes };
        setSessionData(LANG_DATA_TEXT, updated);
        return updated;
      });
      stopLoading()
    } catch (err) {
      navigate('/')
    }
  }, [navigate]);


  useEffect(() => {
    if (!configData?.[CONFIG_KEY?.GLOBAL_CONFIG]) {
      fetchConfigData(CONFIG_KEY?.GLOBAL_CONFIG);
    }
  }, []);

  useEffect(() => {
    const selectedLang = selectedLanguage?.toLowerCase();
    if (!langData?.[selectedLang]) {
      fetchLangData(selectedLang);
    }
  }, [selectedLanguage, fetchLangData])

  useEffect(() => {
    productCodes.forEach((code, index) => {
      !configData?.[code] && fetchConfigData(code, index > 0);
    });
    configData?.[productCode] && stopLoading();
  }, [productCodesKey]);

  const contextValue = useMemo(() => {
    return ({
      langData: langData?.[selectedLanguage?.toLowerCase()] ?? {},
      configData: productCodes.length > 1 ?
        productCodes.reduce((pageConfig, code) => ({ ...pageConfig, ...configData?.[code] }), {}) :
        configData?.[productCode] ?? configData,
      loading: pendingRequests > 1,
      globalConfig: configData?.[CONFIG_KEY?.GLOBAL_CONFIG]
    })
  }, [langData, configData, productCodes, productCode, selectedLanguage, pendingRequests]);


  return (
    <TransAndConfigContext.Provider
      value={contextValue}
    >
      {children}
    </TransAndConfigContext.Provider>
  );
};